import React, { useEffect, useState } from 'react';
import { customerHomePath } from '../../lib/customerHome';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, ShoppingBag, ChevronRight } from 'lucide-react';
import { get } from '../../lib/api'; 
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { connectSocket } from '../../lib/socket';
import { orderReference } from '../../lib/orderReference';

const Orders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = () => {
    if (location.state?.fromProfile) navigate(-1);
    else navigate('/profile', { replace: true });
  };
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'PAID':
      case 'PREPARING':
      case 'READY':
      case 'PENDING_PAYMENT':
        return 'bg-blue-100 text-blue-700';
      case 'CANCELLED':
      case 'REJECTED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (order) => {
    if (order.status === 'PENDING' || order.status === 'PAID') return 'Order is placed';
    if (order.status === 'READY' && order.orderType === 'DELIVERY') return 'Waiting for rider';
    if (order.status === 'READY') return 'Ready for pickup';
    return order.status?.replaceAll('_', ' ') || 'Unknown';
  };

  useEffect(() => {
    let active = true;
    const fetchOrders = async () => {
      try {
        const response = await get('/orders'); // Assuming '/orders' is the endpoint for customer orders
        if (response.success && Array.isArray(response.orders)) {
          // Sort orders by date, newest first
          const sortedOrders = response.orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (active) setOrders(sortedOrders);
        } else {
          if (active) setError(response.message || 'Failed to fetch orders.');
        }
      } catch (err) {
        console.error('Failed to fetch orders:', err);
        if (active) setError(err.response?.data?.message || 'An error occurred while fetching orders.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchOrders();
    const socket = connectSocket();
    socket.on('order:update', fetchOrders);
    const timer = setInterval(fetchOrders, 30000);
    return () => {
      active = false;
      clearInterval(timer);
      socket.off('order:update', fetchOrders);
    };
  }, []);

  if (loading) {
    return <div className="max-w-4xl mx-auto space-y-3"><header className="flex items-center gap-2">
        <button type="button" onClick={handleBack} aria-label="Back to profile" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>
        <h1 className="text-lg font-semibold">My Orders</h1>
      </header><p className="text-center">Loading orders...</p></div>;
  }

  if (error) {
    return <div className="max-w-4xl mx-auto space-y-3"><header className="flex items-center gap-2">
        <button type="button" onClick={handleBack} aria-label="Back to profile" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>
        <h1 className="text-lg font-semibold">My Orders</h1>
      </header><p className="text-center text-red-500">{error}</p></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      <header className="flex items-center gap-2">
        <button type="button" onClick={handleBack} aria-label="Back to profile" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>
        <h1 className="text-lg font-semibold">My Orders</h1>
      </header>

      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border bg-white">
        {orders.length > 0 ? (
          orders.map((order) => (
            <Link to={`/orders/${order._id}`} key={order._id} className="group block p-3 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-primary">
              <div>
                <div>
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="flex min-w-0 gap-2">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-gray-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg group-hover:text-primary transition-colors">{order.vendorId?.storeName || 'Unknown Vendor'}</h3>
                        <p className="text-sm text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString()} • #{orderReference(order._id)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={cn("text-xs font-bold px-2 py-1 rounded-full", getStatusColor(order.status))}>
                        {getStatusLabel(order)}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    <div className="text-sm text-gray-600">
                      {order.items && order.items.length > 0
                        ? order.items.map((item) => `${item.qty}x ${item.name}`).join(', ')
                        : 'No items'}
                    </div>
                    <div className="text-sm text-gray-600 flex flex-wrap gap-3">
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                        {order.paymentmethodUsed || 'Unknown payment'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold">₱{order.pricing?.total?.toFixed(2) ?? '0.00'}</span>
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))
        ) : (
          <Card className="rounded-none border-0 p-4 text-center space-y-2">
            <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-gray-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">No orders yet</h2>
              <p className="text-gray-500">Looks like you haven't placed any orders.</p>
            </div>
            <Link to={customerHomePath}>
              <Button variant="outline">Start Shopping</Button>
            </Link>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Orders;
